// src/pages/admin/AdminSettings.jsx
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'

/**
 * admin_settings 테이블 구조 (예시)
 * id: text primary key (예: 'global')
 * retention_days: integer
 * notice_banner: text
 * auto_hide_past_jobs_days: integer
 */
const SETTINGS_ID = 'global'

export default function AdminSettings() {
  const [retention, setRetention] = useState(90) // 파일 보존일
  const [notice, setNotice] = useState('')       // 공지 배너 문구
  const [autoHideDays, setAutoHideDays] = useState(0) // 지난 공고 자동 숨김 (일)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  // 최초 로딩 시 설정 불러오기
  useEffect(() => {
    ; (async () => {
      setLoading(true)
      setErrorMsg('')
      try {
        const { data, error } = await supabase
          .from('admin_settings')
          .select('retention_days, notice_banner, auto_hide_past_jobs_days')
          .eq('id', SETTINGS_ID)
          .maybeSingle()

        if (error) {
          console.error('admin_settings load error', error)
          setErrorMsg('설정 값을 불러오는 중 오류가 발생했습니다. (테이블 미생성일 수도 있습니다)')
        } else if (data) {
          if (typeof data.retention_days === 'number') {
            setRetention(data.retention_days)
          }
          if (typeof data.notice_banner === 'string') {
            setNotice(data.notice_banner)
          }
          if (typeof data.auto_hide_past_jobs_days === 'number') {
            setAutoHideDays(data.auto_hide_past_jobs_days)
          }
        }
      } catch (e) {
        console.error(e)
        setErrorMsg('설정 값을 불러오는 중 알 수 없는 오류가 발생했습니다.')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const save = async () => {
    setSaving(true)
    setErrorMsg('')
    setSuccessMsg('')
    try {
      const { error } = await supabase
        .from('admin_settings')
        .upsert(
          {
            id: SETTINGS_ID,
            retention_days: retention,
            notice_banner: notice,
            auto_hide_past_jobs_days: autoHideDays,
            updated_at: new Date().toISOString()
          },
          { onConflict: 'id' }
        )

      if (error) {
        console.error('admin_settings save error', error)
        setErrorMsg('설정 저장 중 오류가 발생했습니다.')
        return
      }

      setSuccessMsg('설정이 저장되었습니다.')
    } catch (e) {
      console.error(e)
      setErrorMsg('설정 저장 중 알 수 없는 오류가 발생했습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="grid gap-4">
      <h2 className="text-lg font-semibold">⚙️ 설정</h2>

      {loading && (
        <div className="text-sm text-gray-500">설정 값을 불러오는 중…</div>
      )}

      {errorMsg && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      {successMsg && (
        <div className="rounded border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
          {successMsg}
        </div>
      )}

      {/* 파일 보존 기간 */}
      <div className="rounded-2xl border bg-white p-4 grid gap-2">
        <label className="text-sm font-medium">파일 보존 기간(일)</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            className="input w-40"
            value={retention}
            onChange={e => setRetention(parseInt(e.target.value || '0', 10))}
            min={0}
          />
          <span className="text-xs text-gray-500">
            * 지원서/첨부파일을 몇 일까지 보관할지에 대한 기준값입니다.
          </span>
        </div>
      </div>

      {/* 지난 공고 자동 숨김 설정 (AdminJobs / 관리자 대시보드에서 참고용) */}
      <div className="rounded-2xl border bg-white p-4 grid gap-2">
        <label className="text-sm font-medium">
          지난 공고 자동 숨김 기준(일)
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            className="input w-40"
            value={autoHideDays}
            onChange={e => setAutoHideDays(parseInt(e.target.value || '0', 10))}
            min={0}
          />
          <span className="text-xs text-gray-500">
            * 0일이면 모든 공고를 표시합니다. 예: 7일 → 7일 이전에 종료된 공고는
            Admin 공고/매칭 화면에서 기본 필터로 숨기는 데 참고할 수 있습니다.
          </span>
        </div>
        <p className="text-[11px] text-gray-400">
          (실제 숨김 동작은 <code>AdminJobs.jsx</code>나 관리자 대시보드에서 이 값을 읽어 필터링하도록 구현하면 됩니다.)
        </p>
      </div>

      {/* 공지 배너 문구 */}
      <div className="rounded-2xl border bg-white p-4 grid gap-3">
        <label className="text-sm font-medium">공지 배너 문구</label>
        <textarea
          className="input min-h-[100px]"
          placeholder="상단 공지에 노출할 문구를 입력하세요."
          value={notice}
          onChange={e => setNotice(e.target.value)}
        />
        <p className="text-[11px] text-gray-400">
          예) &quot;현재 서비스 베타 오픈 중입니다. 오류 발생 시
          floater.0601@gmail.com 으로 문의 주세요.&quot;
        </p>
      </div>

      <div>
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 text-sm font-semibold"
        >
          {saving ? '저장 중…' : '저장'}
        </button>
      </div>
    </div>
  )
}
